import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowLeft, Cog, Factory, Zap, Gauge, Thermometer,
  Ruler, Droplets, Wind, Beaker, Download, Info,
  Maximize2, Minimize2, ChevronRight, Shield,
  CheckCircle2, AlertCircle, Circle, AlertTriangle
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, Scatter, ScatterChart,
  ReferenceLine, ReferenceDot
} from 'recharts';
import { apiPumpModel, apiPumpModels } from '@/lib/phpApi';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';

interface HQPoint {
  id: string;
  frequencyHz: number | null;
  flowGpm: number | null;
  flowM3h: number | null;
  headFt: number | null;
  headM: number | null;
  pressurePsi: number | null;
  pressureBar: number | null;
  dataPointType: string | null;
}

interface PQPoint {
  id: string;
  flowGpm: number | null;
  flowM3h: number | null;
  bhp: number | null;
  powerKw: number | null;
  efficiencyPct: number | null;
}

interface BubbleData {
  id: string;
  bubbleSizeMinUm: number | null;
  bubbleSizeMaxUm: number | null;
  bubbleSizeMeanUm: number | null;
  generationMethod: string | null;
  airEntrainmentPct: string | null;
  airFlowRateNlMin: number | null;
  saturationEfficiencyPct: string | null;
  pressureReleaseBar: number | null;
}

interface OperatingParams {
  ratedFlowGpm: number | null;
  ratedFlowM3h: number | null;
  ratedPressurePsi: number | null;
  ratedPressureBar: number | null;
  recycleRatioPct: string | null;
  airToSolidsRatio: string | null;
  hydraulicLoading: string | null;
  surfaceLoading: string | null;
  floatDryMatterPct: string | null;
}

interface Dimensions {
  lengthIn: number | null;
  widthIn: number | null;
  heightIn: number | null;
  lengthMm: number | null;
  widthMm: number | null;
  heightMm: number | null;
  weightLbs: number | null;
  shippingWeightLbs: number | null;
}

interface CurveMeta {
  format: string | null;
  xAxisLabel: string | null;
  yAxisPrimaryLabel: string | null;
  yAxisSecondaryLabel: string | null;
  curveTypesAvailable: string | null;
  testFluid: string | null;
  testTempC: string | null;
  testDensity: number | null;
  testViscosity: number | null;
}

interface PumpModel {
  id: string;
  code: string;
  seriesId: string;
  seriesName: string;
  pumpType: string | null;
  technology: string | null;
  manufacturerId: string;
  manufacturerName: string;
  country: string | null;
  website: string | null;
  motorKw: number | null;
  motorHp: number | null;
  speedRpm: string | null;
  voltageV: string | null;
  phase: string | null;
  enclosure: string | null;
  impellerMaterial: string | null;
  shaftMaterial: string | null;
  casingMaterial: string | null;
  sealType: string | null;
  maxPressureBar: number | null;
  maxFlowM3h: number | null;
  tempRangeC: string | null;
}

interface PumpDetailData {
  model: PumpModel;
  hqCurves: HQPoint[];
  pqCurves: PQPoint[];
  bubbleData: BubbleData[];
  operatingParams: OperatingParams | null;
  dimensions: Dimensions | null;
  curveMeta: CurveMeta | null;
}

// ─── Safe number coercion (PDO can return numeric columns as strings) ───
const n = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const num = Number(v);
  return isNaN(num) ? null : num;
};

// ─── H-Q Curve Interpolation — parabolic pump curve model ────────────────
// Pump H-Q curves follow a parabolic shape: H = a + b·Q + c·Q²
// (affinity law: H ∝ Q² for fixed impeller). Linear interpolation between
// sparse data points overestimates head in concave-down regions.
// For 3+ points: least-squares quadratic fit H = a + b·Q + c·Q²
// For 2 points: H = H₀ − k·Q² (forced-zero-slope parabola through both points)
// For 1 point: constant rated point
function interpolateHQCurve(points: HQPoint[]): Array<{ flowM3h: number; headM: number; label: string }> {
  const sorted = [...points]
    .map(p => ({ ...p, flowM3h: n(p.flowM3h), headM: n(p.headM) }))
    .filter((p): p is typeof p & { flowM3h: number; headM: number } => p.flowM3h !== null && p.headM !== null)
    .sort((a, b) => a.flowM3h - b.flowM3h);

  if (sorted.length === 0) return [];
  if (sorted.length === 1) {
    return [{ flowM3h: sorted[0].flowM3h, headM: sorted[0].headM, label: sorted[0].dataPointType || 'Rated' }];
  }

  const result: Array<{ flowM3h: number; headM: number; label: string }> = [];
  const minFlow = sorted[0].flowM3h;
  const maxFlow = sorted[sorted.length - 1].flowM3h;
  const steps = 40;

  // ── Fit quadratic coefficients ──────────────────────────────────────────
  let a = 0, b = 0, c = 0;

  if (sorted.length === 2) {
    // Two-point parabola: H = H₀ - k·Q²  where k = (H₀ - H₁) / Q₁²
    // We use the first point as H₀ (shut-off head extrapolation) and pass through both.
    // Solve: H(Q₀) = a - c·Q₀²,  H(Q₁) = a - c·Q₁²  → system of 2 eqs, b=0
    const Q0 = sorted[0].flowM3h, H0 = sorted[0].headM;
    const Q1 = sorted[1].flowM3h, H1 = sorted[1].headM;
    const dQ2 = Q1 * Q1 - Q0 * Q0;
    c = dQ2 !== 0 ? (H1 - H0) / dQ2 : 0;
    a = H0 - c * Q0 * Q0;
    b = 0;
  } else {
    // 3+ points: least-squares quadratic regression H = a + b·Q + c·Q²
    const m = sorted.length;
    let sQ = 0, sQ2 = 0, sQ3 = 0, sQ4 = 0, sH = 0, sQH = 0, sQ2H = 0;
    for (const pt of sorted) {
      const q = pt.flowM3h, h = pt.headM;
      sQ += q; sQ2 += q*q; sQ3 += q*q*q; sQ4 += q*q*q*q;
      sH += h; sQH += q*h; sQ2H += q*q*h;
    }
    // Normal equations matrix [A][x] = [B]
    // | m     sQ    sQ2 | | a |   | sH    |
    // | sQ    sQ2   sQ3 | | b | = | sQH   |
    // | sQ2   sQ3   sQ4 | | c |   | sQ2H  |
    const A = [[m, sQ, sQ2], [sQ, sQ2, sQ3], [sQ2, sQ3, sQ4]];
    const Bv = [sH, sQH, sQ2H];
    // Gaussian elimination
    for (let col = 0; col < 3; col++) {
      let maxRow = col;
      for (let row = col + 1; row < 3; row++) if (Math.abs(A[row][col]) > Math.abs(A[maxRow][col])) maxRow = row;
      [A[col], A[maxRow]] = [A[maxRow], A[col]];
      [Bv[col], Bv[maxRow]] = [Bv[maxRow], Bv[col]];
      for (let row = col + 1; row < 3; row++) {
        const f = A[row][col] / (A[col][col] || 1e-12);
        for (let k2 = col; k2 < 3; k2++) A[row][k2] -= f * A[col][k2];
        Bv[row] -= f * Bv[col];
      }
    }
    const coeffs = [0, 0, 0];
    for (let i = 2; i >= 0; i--) {
      let s = Bv[i];
      for (let j = i + 1; j < 3; j++) s -= A[i][j] * coeffs[j];
      coeffs[i] = s / (A[i][i] || 1e-12);
    }
    [a, b, c] = coeffs;
  }

  for (let i = 0; i <= steps; i++) {
    const flow = minFlow + (maxFlow - minFlow) * (i / steps);
    const head = Math.max(0, a + b * flow + c * flow * flow);
    result.push({ flowM3h: parseFloat(flow.toFixed(2)), headM: parseFloat(head.toFixed(2)), label: 'Curve' });
  }

  // Add actual data points with labels
  sorted.forEach(p => {
    result.push({ flowM3h: p.flowM3h, headM: p.headM, label: p.dataPointType || 'Data' });
  });

  return result;
}

function interpolatePQCurve(points: PQPoint[]): Array<{ flowM3h: number; powerKw: number; label: string }> {
  const sorted = [...points]
    .map(p => ({ ...p, flowM3h: n(p.flowM3h), powerKw: n(p.powerKw) }))
    .filter((p): p is typeof p & { flowM3h: number; powerKw: number } => p.flowM3h !== null && p.powerKw !== null)
    .sort((a, b) => a.flowM3h - b.flowM3h);

  if (sorted.length === 0) return [];
  if (sorted.length === 1) {
    return [{ flowM3h: sorted[0].flowM3h!, powerKw: sorted[0].powerKw!, label: 'Rated' }];
  }

  const result: Array<{ flowM3h: number; powerKw: number; label: string }> = [];
  const minFlow = sorted[0].flowM3h!;
  const maxFlow = sorted[sorted.length - 1].flowM3h!;
  const steps = 30;

  for (let i = 0; i <= steps; i++) {
    const flow = minFlow + (maxFlow - minFlow) * (i / steps);
    let power = 0;
    for (let j = 0; j < sorted.length - 1; j++) {
      const p1 = sorted[j];
      const p2 = sorted[j + 1];
      if (flow >= p1.flowM3h! && flow <= p2.flowM3h!) {
        const t = (flow - p1.flowM3h!) / (p2.flowM3h! - p1.flowM3h!);
        // Power ∝ Q³ affinity law for shape, blended with linear
        power = p1.powerKw! + (p2.powerKw! - p1.powerKw!) * (t * t * t * 0.5 + t * 0.5);
        break;
      }
    }
    if (i === 0) power = sorted[0].powerKw!;
    if (i === steps) power = sorted[sorted.length - 1].powerKw!;

    result.push({ flowM3h: parseFloat(flow.toFixed(2)), powerKw: parseFloat(power.toFixed(2)), label: 'Interpolated' });
  }

  sorted.forEach(p => {
    result.push({ flowM3h: p.flowM3h!, powerKw: p.powerKw!, label: 'Data' });
  });

  return result;
}

// ─── SVG Curve Renderer ─────────────────────────────────
interface CurvePoint {
  flowM3h: number;
  headM?: number;
  powerKw?: number;
}

function SvgCurve({ data, xKey, yKey, color, yLabel, filename, opPoint }: {
  data: CurvePoint[];
  xKey: keyof CurvePoint;
  yKey: keyof CurvePoint;
  color: string;
  yLabel: string;
  filename: string;
  opPoint?: { x: number; y: number; label?: string; color?: string } | null;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  if (data.length === 0) return <p className="text-xs text-slate-500">No data</p>;

  const xVals = data.map(d => d[xKey] as number);
  const yVals = data.map(d => d[yKey] as number);
  const xMin = Math.min(...xVals);
  const xMax = Math.max(...xVals);
  const yMin = Math.min(...yVals);
  const yMax = Math.max(...yVals);
  const yPad = (yMax - yMin) * 0.1 || 1;

  const scaleX = (v: number) => ((v - xMin) / (xMax - xMin || 1)) * 440 + 30;
  const scaleY = (v: number) => 260 - ((v - (yMin - yPad)) / ((yMax + yPad) - (yMin - yPad) || 1)) * 220;

  const pathD = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${scaleX(d[xKey] as number)} ${scaleY(d[yKey] as number)}`).join(' ');

  const exportSvg = () => {
    if (!svgRef.current) return;
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgRef.current);
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('SVG exported');
  };

  // Grid lines
  const gridLinesX = Array.from({ length: 6 }, (_, i) => xMin + (xMax - xMin) * (i / 5));
  const gridLinesY = Array.from({ length: 6 }, (_, i) => (yMin - yPad) + ((yMax + yPad) - (yMin - yPad)) * (i / 5));

  return (
    <div>
      <svg ref={svgRef} viewBox="0 0 500 300" className="w-full h-80 bg-slate-900 rounded border border-slate-800">
        {/* Grid */}
        {gridLinesX.map((v, i) => (
          <line key={`gx${i}`} x1={scaleX(v)} y1={40} x2={scaleX(v)} y2={260} stroke="#1e293b" strokeWidth="1" />
        ))}
        {gridLinesY.map((v, i) => (
          <line key={`gy${i}`} x1={30} y1={scaleY(v)} x2={470} y2={scaleY(v)} stroke="#1e293b" strokeWidth="1" />
        ))}
        {/* Axes */}
        <line x1="30" y1="260" x2="470" y2="260" stroke="#475569" strokeWidth="1.5" />
        <line x1="30" y1="40" x2="30" y2="260" stroke="#475569" strokeWidth="1.5" />
        {/* Labels */}
        <text x="250" y="290" fill="#94a3b8" fontSize="11" textAnchor="middle">Flow (m³/h)</text>
        <text x="12" y="150" fill="#94a3b8" fontSize="11" textAnchor="middle" transform="rotate(-90 12 150)">{yLabel}</text>
        {/* X tick labels */}
        {gridLinesX.map((v, i) => (
          <text key={`tx${i}`} x={scaleX(v)} y="275" fill="#64748b" fontSize="9" textAnchor="middle">{v.toFixed(1)}</text>
        ))}
        {/* Y tick labels */}
        {gridLinesY.map((v, i) => (
          <text key={`ty${i}`} x="22" y={scaleY(v) + 3} fill="#64748b" fontSize="9" textAnchor="end">{v.toFixed(1)}</text>
        ))}
        {/* Curve */}
        <path d={pathD} fill="none" stroke={color} strokeWidth="2" />
        {/* Points */}
        {data.map((d, i) => (
          <circle key={i} cx={scaleX(d[xKey] as number)} cy={scaleY(d[yKey] as number)} r="3" fill={color} />
        ))}
        {/* Operating Point */}
        {opPoint && (
          <>
            <line x1={scaleX(opPoint.x)} y1={40} x2={scaleX(opPoint.x)} y2={260} stroke="#94a3b8" strokeWidth="1" strokeDasharray="4 4" opacity="0.5" />
            <circle cx={scaleX(opPoint.x)} cy={scaleY(opPoint.y)} r="5" fill={opPoint.color || '#f59e0b'} stroke="#fff" strokeWidth="2" />
            <text x={scaleX(opPoint.x)} y={scaleY(opPoint.y) - 10} fill={opPoint.color || '#f59e0b'} fontSize="9" textAnchor="middle">{opPoint.label || 'Op. Point'}</text>
          </>
        )}
      </svg>
      <div className="flex justify-end mt-2">
        <Button variant="outline" size="sm" onClick={exportSvg} className="border-slate-700 text-slate-300 hover:text-white text-xs">
          <Download className="w-3 h-3 mr-1" /> Export SVG
        </Button>
      </div>
    </div>
  );
}

// ─── Custom Tooltip ─────────────────────────────────────
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs text-slate-400 mb-1">Flow: {label} m³/h</p>
      {payload.map((p: any, i: number) => (
        <p key={i} className="text-sm font-medium" style={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toFixed(2) : p.value}
        </p>
      ))}
    </div>
  );
}

export default function PumpDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PumpDetailData | null>(null);
  const [siblingModels, setSiblingModels] = useState<PumpModel[]>([]);
  const [curveFullscreen, setCurveFullscreen] = useState(false);

  useEffect(() => {
    if (id) loadData(id);
  }, [id]);

  async function loadData(modelId: string) {
    setLoading(true);
    try {
      const detail = await apiPumpModel(modelId);
      setData(detail);
      // Load sibling models in same series for comparison
      if (detail.model?.seriesId) {
        const siblings = await apiPumpModels({ seriesId: detail.model.seriesId });
        setSiblingModels((siblings.models || []).filter((m: PumpModel) => m.id !== modelId));
      }
    } catch (err) {
      toast.error('Failed to load pump details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const hqCurveData = useMemo(() => {
    if (!data?.hqCurves?.length) return [];
    return interpolateHQCurve(data.hqCurves);
  }, [data?.hqCurves]);

  const pqCurveData = useMemo(() => {
    if (!data?.pqCurves?.length) return [];
    return interpolatePQCurve(data.pqCurves);
  }, [data?.pqCurves]);

  const hasHQCurve = hqCurveData.length > 0;
  const hasPQCurve = pqCurveData.length > 0;

  const exportCurveData = useCallback(() => {
    if (!data) return;
    const exportObj = {
      model: data.model,
      hqCurve: data.hqCurves,
      pqCurve: data.pqCurves,
      bubbleData: data.bubbleData,
      operatingParams: data.operatingParams,
      dimensions: data.dimensions,
      curveMeta: data.curveMeta,
      interpolatedHQ: hqCurveData,
      interpolatedPQ: pqCurveData,
      exportedAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(exportObj, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pump-${data.model.code.replace(/[^a-zA-Z0-9]/g, '_')}-data.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Pump data exported');
  }, [data, hqCurveData, pqCurveData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-200 p-4 md:p-6">
        <div className="max-w-6xl mx-auto space-y-4">
          <Skeleton className="h-10 w-64 bg-slate-800/60" />
          <Skeleton className="h-96 bg-slate-800/60" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-200 p-4 md:p-6 flex items-center justify-center">
        <div className="text-center">
          <Cog className="w-12 h-12 mx-auto mb-3 text-slate-600" />
          <p className="text-lg font-medium">Pump not found</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/pumps')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Pump List
          </Button>
        </div>
      </div>
    );
  }

  const { model, bubbleData, operatingParams, dimensions, curveMeta } = data;

  // ─── DAF Pump Selection ───
  const dafParamsRaw = localStorage.getItem('daf-params-v2');
  const dafParams = dafParamsRaw ? JSON.parse(dafParamsRaw) : null;
  const requiredFlow = dafParams ? (dafParams.flowRate / 24) * (dafParams.recycleRate / 100) : 0;
  const requiredPressure = dafParams ? dafParams.saturatorPressure : 0;
  const isSelected = dafParams?.selectedPumpId === model.id;
  const isValid = isSelected && model.maxFlowM3h && model.maxPressureBar &&
    requiredFlow <= model.maxFlowM3h * 1.1 &&
    requiredFlow >= model.maxFlowM3h * 0.35 &&
    requiredPressure <= model.maxPressureBar * 1.05;
  const isOversized = isSelected && model.maxFlowM3h && requiredFlow < model.maxFlowM3h * 0.35;
  const isUndersized = isSelected && model.maxFlowM3h && requiredFlow > model.maxFlowM3h * 1.1;

  // ─── Operating Point Analysis ───
  // Convert required pressure (bar) to head (m): 1 bar ≈ 10.197 m H₂O
  const requiredHeadM = requiredPressure > 0 ? requiredPressure * 10.197 : 0;

  // Interpolate head at required flow from H-Q curve
  function interpolateHeadAtFlow(flow: number, curve: typeof hqCurveData): number | null {
    // Read the curve points and use the same parabolic model as interpolateHQCurve
    // Filter to actual data points (not interpolated)
    const dataPts = curve.filter(p => p.label !== 'Curve').sort((a, b) => a.flowM3h - b.flowM3h);
    if (dataPts.length === 0) {
      // Fall back to curve points if no raw data tagged
      const allPts = [...curve].sort((a, b) => a.flowM3h - b.flowM3h);
      if (allPts.length === 0) return null;
      if (flow <= allPts[0].flowM3h) return allPts[0].headM;
      if (flow >= allPts[allPts.length - 1].flowM3h) return allPts[allPts.length - 1].headM;
      // Linear fallback on curve data
      for (let i = 0; i < allPts.length - 1; i++) {
        if (flow >= allPts[i].flowM3h && flow <= allPts[i+1].flowM3h) {
          const t = (flow - allPts[i].flowM3h) / (allPts[i+1].flowM3h - allPts[i].flowM3h);
          return allPts[i].headM + (allPts[i+1].headM - allPts[i].headM) * t;
        }
      }
      return null;
    }
    // Use parabolic coefficients (same approach as interpolateHQCurve)
    let a = 0, b = 0, c = 0;
    if (dataPts.length === 1) return dataPts[0].headM;
    if (dataPts.length === 2) {
      const Q0 = dataPts[0].flowM3h, H0 = dataPts[0].headM;
      const Q1 = dataPts[1].flowM3h, H1 = dataPts[1].headM;
      const dQ2 = Q1*Q1 - Q0*Q0;
      c = dQ2 !== 0 ? (H1 - H0) / dQ2 : 0;
      a = H0 - c * Q0 * Q0; b = 0;
    } else {
      const m = dataPts.length;
      let sQ=0,sQ2=0,sQ3=0,sQ4=0,sH=0,sQH=0,sQ2H=0;
      for (const pt of dataPts) { const q=pt.flowM3h,h=pt.headM; sQ+=q;sQ2+=q*q;sQ3+=q*q*q;sQ4+=q*q*q*q;sH+=h;sQH+=q*h;sQ2H+=q*q*h; }
      const A=[[m,sQ,sQ2],[sQ,sQ2,sQ3],[sQ2,sQ3,sQ4]];const Bv=[sH,sQH,sQ2H];
      for(let col=0;col<3;col++){let mx=col;for(let r=col+1;r<3;r++)if(Math.abs(A[r][col])>Math.abs(A[mx][col]))mx=r;[A[col],A[mx]]=[A[mx],A[col]];[Bv[col],Bv[mx]]=[Bv[mx],Bv[col]];for(let r=col+1;r<3;r++){const f=A[r][col]/(A[col][col]||1e-12);for(let k=col;k<3;k++)A[r][k]-=f*A[col][k];Bv[r]-=f*Bv[col];}}
      const cf=[0,0,0];for(let i=2;i>=0;i--){let s=Bv[i];for(let j=i+1;j<3;j++)s-=A[i][j]*cf[j];cf[i]=s/(A[i][i]||1e-12);}
      [a,b,c]=cf;
    }
    return Math.max(0, a + b * flow + c * flow * flow);
  }

  const actualHeadAtOp = requiredFlow > 0 && hasHQCurve ? interpolateHeadAtFlow(requiredFlow, hqCurveData) : null;
  const headMargin = actualHeadAtOp && requiredHeadM > 0 ? actualHeadAtOp - requiredHeadM : null;
  const canMeetHead = headMargin !== null && headMargin >= 0;

  // Interpolate power at required flow from P-Q curve
  function interpolatePowerAtFlow(flow: number, curve: typeof pqCurveData): number | null {
    if (curve.length === 0) return null;
    const sorted = [...curve].sort((a, b) => a.flowM3h - b.flowM3h);
    for (let i = 0; i < sorted.length - 1; i++) {
      const p1 = sorted[i];
      const p2 = sorted[i + 1];
      if (flow >= p1.flowM3h && flow <= p2.flowM3h) {
        const t = (flow - p1.flowM3h) / (p2.flowM3h - p1.flowM3h);
        return p1.powerKw + (p2.powerKw - p1.powerKw) * t;
      }
    }
    if (flow < sorted[0].flowM3h) return sorted[0].powerKw;
    if (flow > sorted[sorted.length - 1].flowM3h) return sorted[sorted.length - 1].powerKw;
    return null;
  }

  const actualPowerAtOp = requiredFlow > 0 && hasPQCurve ? interpolatePowerAtFlow(requiredFlow, pqCurveData) : null;

  const handleSelectPump = () => {
    const updated = {
      ...dafParams,
      selectedPumpId: model.id,
      selectedPumpCode: model.code,
      selectedPumpMaxFlowM3h: model.maxFlowM3h,
      selectedPumpMaxPressureBar: model.maxPressureBar,
      selectedPumpMotorKw: model.motorKw,
    };
    localStorage.setItem('daf-params-v2', JSON.stringify(updated));
    toast.success(`${model.code} selected for DAF spec`);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-4 md:p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-6xl mx-auto space-y-6"
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div>
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
              <button
                onClick={() => navigate('/daf-spec')}
                className="flex items-center gap-1 hover:text-sky-400 transition-colors"
              >
                <ArrowLeft className="w-3 h-3" /> DAF Specification
              </button>
              <span className="text-slate-400">/</span>
              <button
                onClick={() => navigate('/pumps')}
                className="hover:text-sky-400 transition-colors"
              >
                Pump Database
              </button>
              <span className="text-slate-400">/</span>
              <span className="text-slate-300 font-medium">{model.code}</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-bold text-white">{model.code}</h1>
              <Badge variant="outline" className="border-sky-500/30 text-sky-400">
                {model.manufacturerName}
              </Badge>
              {model.country && (
                <Badge variant="outline" className="border-slate-600 text-slate-400">
                  {model.country}
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-400 mt-1">
              {model.seriesName} · {model.pumpType} · {model.technology}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Status Dot */}
            {dafParams && (
              <div className="flex items-center gap-1.5 mr-2">
                {isSelected ? (
                  isValid ? (
                    <><div className="w-2.5 h-2.5 rounded-full bg-emerald-400" /><span className="text-[10px] text-emerald-400">Active in DAF</span></>
                  ) : isUndersized || (model.maxPressureBar && requiredPressure > model.maxPressureBar * 1.05) ? (
                    <><div className="w-2.5 h-2.5 rounded-full bg-rose-400" /><span className="text-[10px] text-rose-400">Incompatible</span></>
                  ) : isOversized ? (
                    <><div className="w-2.5 h-2.5 rounded-full bg-amber-400" /><span className="text-[10px] text-amber-400">Oversized</span></>
                  ) : (
                    <><div className="w-2.5 h-2.5 rounded-full bg-yellow-400" /><span className="text-[10px] text-yellow-400">Selected</span></>
                  )
                ) : (
                  <><div className="w-2.5 h-2.5 rounded-full bg-rose-400" /><span className="text-[10px] text-rose-400">Not in DAF</span></>
                )}
              </div>
            )}
            <Button
              variant={isSelected ? "default" : "outline"}
              size="sm"
              onClick={(e) => { e.stopPropagation(); handleSelectPump(); }}
              className={isSelected ? "bg-yellow-500/20 text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/30" : "border-slate-700 text-slate-300 hover:text-white"}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {isSelected ? 'Selected for DAF' : 'Select for DAF'}
            </Button>
            <Button variant="outline" size="sm" onClick={exportCurveData} className="border-slate-700 text-slate-300 hover:text-white">
              <Download className="w-4 h-4 mr-2" /> Export Data
            </Button>
          </div>
        </div>

        {/* Key Specs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {model.motorKw !== null && (
            <Card className="bg-rb-surface border-slate-800/50">
              <CardContent className="p-3 flex items-center gap-3">
                <Zap className="w-5 h-5 text-amber-400" />
                <div>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Motor Power</p>
                  <p className="text-sm font-bold text-white">{model.motorKw} kW</p>
                  {model.motorHp !== null && <p className="text-[10px] text-slate-400">{model.motorHp} HP</p>}
                </div>
              </CardContent>
            </Card>
          )}
          {model.maxPressureBar !== null && (
            <Card className="bg-rb-surface border-slate-800/50">
              <CardContent className="p-3 flex items-center gap-3">
                <Gauge className="w-5 h-5 text-emerald-400" />
                <div>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Max Pressure</p>
                  <p className="text-sm font-bold text-white">{model.maxPressureBar} bar</p>
                </div>
              </CardContent>
            </Card>
          )}
          {model.maxFlowM3h !== null && (
            <Card className="bg-rb-surface border-slate-800/50">
              <CardContent className="p-3 flex items-center gap-3">
                <Droplets className="w-5 h-5 text-sky-400" />
                <div>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Max Flow</p>
                  <p className="text-sm font-bold text-white">{model.maxFlowM3h} m³/h</p>
                </div>
              </CardContent>
            </Card>
          )}
          {model.speedRpm && (
            <Card className="bg-rb-surface border-slate-800/50">
              <CardContent className="p-3 flex items-center gap-3">
                <Cog className="w-5 h-5 text-purple-400" />
                <div>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">Speed</p>
                  <p className="text-sm font-bold text-white">{model.speedRpm}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* DAF Operating Point Analysis */}
        {dafParams && requiredFlow > 0 && (
          <Card className={`border ${canMeetHead === false || isUndersized ? 'bg-rose-500/5 border-rose-500/20' : isOversized ? 'bg-amber-500/5 border-amber-500/20' : 'bg-emerald-500/5 border-emerald-500/20'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-2.5 h-2.5 rounded-full ${canMeetHead === false || isUndersized ? 'bg-rose-400' : isOversized ? 'bg-amber-400' : 'bg-emerald-400'}`} />
                <h3 className="text-sm font-bold text-white">DAF Operating Point Analysis</h3>
                <span className="text-[10px] text-slate-400 ml-auto">
                  From saved DAF spec: {dafParams.flowRate} m³/d @ {dafParams.recycleRate}% recycle
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-800/40 rounded-lg p-2.5">
                  <p className="text-[10px] text-slate-500 uppercase font-bold">Required Flow</p>
                  <p className="text-sm font-bold text-white">{requiredFlow.toFixed(1)} <span className="text-xs text-slate-400">m³/h</span></p>
                </div>
                <div className="bg-slate-800/40 rounded-lg p-2.5">
                  <p className="text-[10px] text-slate-500 uppercase font-bold">Required Head</p>
                  <p className="text-sm font-bold text-white">{requiredHeadM.toFixed(1)} <span className="text-xs text-slate-400">m</span></p>
                  <p className="text-[10px] text-slate-400">({requiredPressure} bar)</p>
                </div>
                {actualHeadAtOp !== null && (
                  <div className="bg-slate-800/40 rounded-lg p-2.5">
                    <p className="text-[10px] text-slate-500 uppercase font-bold">Curve Head @ Flow</p>
                    <p className={`text-sm font-bold ${canMeetHead ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {actualHeadAtOp.toFixed(1)} <span className="text-xs text-slate-400">m</span>
                    </p>
                    <p className="text-[10px]">
                      {headMargin && headMargin >= 0
                        ? <span className="text-emerald-400">+{headMargin.toFixed(1)} m margin</span>
                        : <span className="text-rose-400">{headMargin?.toFixed(1)} m short</span>
                      }
                    </p>
                  </div>
                )}
                {actualPowerAtOp !== null && (
                  <div className="bg-slate-800/40 rounded-lg p-2.5">
                    <p className="text-[10px] text-slate-500 uppercase font-bold">Est. Power @ Op.</p>
                    <p className="text-sm font-bold text-sky-400">{actualPowerAtOp.toFixed(2)} <span className="text-xs text-slate-400">kW</span></p>
                    {model.motorKw && (
                      <p className="text-[10px] text-slate-400">
                        Motor: {model.motorKw} kW ({((actualPowerAtOp / model.motorKw) * 100).toFixed(0)}% load)
                      </p>
                    )}
                  </div>
                )}
                {!hasHQCurve && (
                  <div className="bg-slate-800/40 rounded-lg p-2.5 col-span-2 md:col-span-1">
                    <p className="text-[10px] text-slate-500 uppercase font-bold">Curve Data</p>
                    <p className="text-xs text-amber-400">No H-Q curve available</p>
                  </div>
                )}
              </div>
              {canMeetHead === false && (
                <p className="text-[10px] text-rose-400 mt-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Pump cannot meet required head at operating flow. Consider a higher-pressure model or reducing saturator pressure.
                </p>
              )}
              {isOversized && (
                <p className="text-[10px] text-amber-400 mt-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Pump is oversized for this DAF. Operating far left on curve reduces efficiency and may cause cavitation.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Curves */}
        {(hasHQCurve || hasPQCurve) && (
          <Card className={`bg-rb-surface border-slate-800/50 ${curveFullscreen ? 'fixed inset-4 z-50' : ''}`}>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-bold text-white flex items-center gap-2">
                <Gauge className="w-4 h-4 text-sky-400" />
                Performance Curves
                {curveMeta && (
                  <Badge variant="outline" className="text-[10px] border-slate-600 text-slate-400 ml-2">
                    {curveMeta.format}
                  </Badge>
                )}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-white"
                  onClick={() => setCurveFullscreen(!curveFullscreen)}
                >
                  {curveFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </Button>
              </div>
            </CardHeader>
            <CardContent className={curveFullscreen ? 'h-[calc(100%-60px)]' : ''}>
              <Tabs defaultValue={hasHQCurve ? 'hq' : 'pq'}>
                <TabsList className="bg-slate-800/60 border border-slate-700 mb-4">
                  {hasHQCurve && <TabsTrigger value="hq" className="text-xs data-[state=active]:bg-sky-500/20 data-[state=active]:text-sky-400">H-Q Curve</TabsTrigger>}
                  {hasPQCurve && <TabsTrigger value="pq" className="text-xs data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">P-Q Curve</TabsTrigger>}
                  <TabsTrigger value="svg" className="text-xs data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400">SVG Export</TabsTrigger>
                  <TabsTrigger value="raw" className="text-xs data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">Raw Data</TabsTrigger>
                </TabsList>

                {hasHQCurve && (
                  <TabsContent value="hq">
                    <div className={`${curveFullscreen ? 'h-full' : 'h-80'}`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={hqCurveData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis
                            dataKey="flowM3h"
                            type="number"
                            stroke="#94a3b8"
                            tick={{ fontSize: 11 }}
                            label={{ value: 'Flow (m³/h)', position: 'insideBottom', offset: -2, fill: 'var(--rb-text-2)', fontSize: 11 }}
                          />
                          <YAxis
                            stroke="#94a3b8"
                            tick={{ fontSize: 11 }}
                            label={{ value: 'Head (m)', angle: -90, position: 'insideLeft', fill: 'var(--rb-text-2)', fontSize: 11 }}
                          />
                          <Tooltip content={<ChartTooltip />} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Line
                            type="monotone"
                            dataKey="headM"
                            name="Head (m)"
                            stroke="#38bdf8"
                            strokeWidth={2}
                            dot={{ r: 3, fill: '#38bdf8' }}
                            activeDot={{ r: 5 }}
                          />
                          {requiredFlow > 0 && requiredHeadM > 0 && actualHeadAtOp !== null && (
                            <ReferenceDot
                              x={requiredFlow}
                              y={actualHeadAtOp}
                              r={6}
                              fill={canMeetHead ? '#10b981' : '#ef4444'}
                              stroke="#fff"
                              strokeWidth={2}
                              label={{ value: 'DAF Op. Point', position: 'top', fill: canMeetHead ? '#10b981' : '#ef4444', fontSize: 10 }}
                            />
                          )}
                          {requiredFlow > 0 && (
                            <ReferenceLine x={requiredFlow} stroke="#94a3b8" strokeDasharray="4 4" strokeOpacity={0.5} />
                          )}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-2 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      Curve interpolated from manufacturer datasheet points using affinity laws.
                    </p>
                  </TabsContent>
                )}

                {hasPQCurve && (
                  <TabsContent value="pq">
                    <div className={`${curveFullscreen ? 'h-full' : 'h-80'}`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={pqCurveData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis
                            dataKey="flowM3h"
                            type="number"
                            stroke="#94a3b8"
                            tick={{ fontSize: 11 }}
                            label={{ value: 'Flow (m³/h)', position: 'insideBottom', offset: -2, fill: 'var(--rb-text-2)', fontSize: 11 }}
                          />
                          <YAxis
                            stroke="#94a3b8"
                            tick={{ fontSize: 11 }}
                            label={{ value: 'Power (kW)', angle: -90, position: 'insideLeft', fill: 'var(--rb-text-2)', fontSize: 11 }}
                          />
                          <Tooltip content={<ChartTooltip />} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Line
                            type="monotone"
                            dataKey="powerKw"
                            name="Power (kW)"
                            stroke="#f59e0b"
                            strokeWidth={2}
                            dot={{ r: 3, fill: '#f59e0b' }}
                            activeDot={{ r: 5 }}
                          />
                          {requiredFlow > 0 && actualPowerAtOp !== null && (
                            <ReferenceDot
                              x={requiredFlow}
                              y={actualPowerAtOp}
                              r={6}
                              fill="#f59e0b"
                              stroke="#fff"
                              strokeWidth={2}
                              label={{ value: 'DAF Op. Point', position: 'top', fill: '#f59e0b', fontSize: 10 }}
                            />
                          )}
                          {requiredFlow > 0 && (
                            <ReferenceLine x={requiredFlow} stroke="#94a3b8" strokeDasharray="4 4" strokeOpacity={0.5} />
                          )}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-2 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      Power curve interpolated using P ∝ Q³ affinity law blending.
                    </p>
                  </TabsContent>
                )}

                {hasHQCurve && (
                  <TabsContent value="svg">
                    <div className="space-y-4">
                      <SvgCurve
                        data={hqCurveData}
                        xKey="flowM3h"
                        yKey="headM"
                        color="#38bdf8"
                        yLabel="Head (m)"
                        filename={`${model.code}_HQ_curve.svg`}
                        opPoint={requiredFlow > 0 && actualHeadAtOp !== null ? { x: requiredFlow, y: actualHeadAtOp, label: 'DAF Op. Point', color: canMeetHead ? '#10b981' : '#ef4444' } : null}
                      />
                      {hasPQCurve && (
                        <SvgCurve
                          data={pqCurveData}
                          xKey="flowM3h"
                          yKey="powerKw"
                          color="#f59e0b"
                          yLabel="Power (kW)"
                          filename={`${model.code}_PQ_curve.svg`}
                          opPoint={requiredFlow > 0 && actualPowerAtOp !== null ? { x: requiredFlow, y: actualPowerAtOp, label: 'DAF Op. Point', color: '#f59e0b' } : null}
                        />
                      )}
                    </div>
                  </TabsContent>
                )}

                <TabsContent value="raw">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left py-2 px-3 text-slate-400 font-medium">Point</th>
                          <th className="text-left py-2 px-3 text-slate-400 font-medium">Flow (m³/h)</th>
                          <th className="text-left py-2 px-3 text-slate-400 font-medium">Head (m)</th>
                          <th className="text-left py-2 px-3 text-slate-400 font-medium">Power (kW)</th>
                          <th className="text-left py-2 px-3 text-slate-400 font-medium">Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.hqCurves.map((p, i) => (
                          <tr key={p.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                            <td className="py-2 px-3 text-slate-300">{p.id}</td>
                            <td className="py-2 px-3 text-slate-300">{p.flowM3h?.toFixed(2) ?? '—'}</td>
                            <td className="py-2 px-3 text-sky-400">{p.headM?.toFixed(1) ?? '—'}</td>
                            <td className="py-2 px-3 text-slate-500">—</td>
                            <td className="py-2 px-3 text-slate-400">{p.dataPointType ?? '—'}</td>
                          </tr>
                        ))}
                        {data.pqCurves.map((p, i) => (
                          <tr key={p.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                            <td className="py-2 px-3 text-slate-300">{p.id}</td>
                            <td className="py-2 px-3 text-slate-300">{p.flowM3h?.toFixed(2) ?? '—'}</td>
                            <td className="py-2 px-3 text-slate-500">—</td>
                            <td className="py-2 px-3 text-amber-400">{p.powerKw?.toFixed(2) ?? '—'}</td>
                            <td className="py-2 px-3 text-slate-400">{p.dataSource ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>
              </Tabs>
              {/* Precision Disclaimer */}
              <div className="mt-4 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                <p className="text-[10px] text-amber-400 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>
                    <strong>Data Precision Notice:</strong> These curves are interpolated from sparse manufacturer catalog data (often single duty points). Affinity-law extrapolation is used where multi-point data is unavailable. These curves are for preliminary selection only — consult OEM datasheets for guaranteed performance envelopes.
                  </span>
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Materials & Construction */}
          <Card className="bg-rb-surface border-slate-800/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold text-white flex items-center gap-2">
                <Factory className="w-4 h-4 text-slate-400" /> Construction
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {[
                { label: 'Impeller', value: model.impellerMaterial, icon: Cog },
                { label: 'Shaft', value: model.shaftMaterial, icon: Ruler },
                { label: 'Casing', value: model.casingMaterial, icon: Factory },
                { label: 'Seal', value: model.sealType, icon: Droplets },
                { label: 'Enclosure', value: model.enclosure, icon: Shield },
                { label: 'Voltage', value: model.voltageV, icon: Zap },
                { label: 'Phase', value: model.phase, icon: Zap },
              ].filter(item => item.value).map((item, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-800/50 last:border-0">
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <item.icon className="w-3.5 h-3.5" />
                    {item.label}
                  </div>
                  <span className="text-xs text-slate-200 font-medium">{item.value}</span>
                </div>
              ))}
              {model.tempRangeC && (
                <div className="flex items-center justify-between py-1.5 border-b border-slate-800/50">
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Thermometer className="w-3.5 h-3.5" />
                    Temp Range
                  </div>
                  <span className="text-xs text-slate-200 font-medium">{model.tempRangeC} °C</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bubble Generation */}
          {bubbleData.length > 0 && (
            <Card className="bg-rb-surface border-slate-800/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-bold text-white flex items-center gap-2">
                  <Wind className="w-4 h-4 text-sky-400" /> Bubble Generation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {bubbleData.map((b, i) => (
                  <div key={b.id} className="space-y-2">
                    {b.generationMethod && (
                      <p className="text-xs text-slate-300">{b.generationMethod}</p>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      {b.bubbleSizeMeanUm !== null && (
                        <div className="bg-slate-800/40 rounded px-2 py-1.5">
                          <p className="text-[10px] text-slate-500">Mean Bubble Size</p>
                          <p className="text-sm font-bold text-sky-400">{b.bubbleSizeMeanUm} μm</p>
                        </div>
                      )}
                      {b.bubbleSizeMinUm !== null && b.bubbleSizeMaxUm !== null && (
                        <div className="bg-slate-800/40 rounded px-2 py-1.5">
                          <p className="text-[10px] text-slate-500">Range</p>
                          <p className="text-sm font-bold text-slate-200">{b.bubbleSizeMinUm}–{b.bubbleSizeMaxUm} μm</p>
                        </div>
                      )}
                      {b.airEntrainmentPct && (
                        <div className="bg-slate-800/40 rounded px-2 py-1.5">
                          <p className="text-[10px] text-slate-500">Air Entrainment</p>
                          <p className="text-sm font-bold text-slate-200">{b.airEntrainmentPct}</p>
                        </div>
                      )}
                      {b.pressureReleaseBar !== null && (
                        <div className="bg-slate-800/40 rounded px-2 py-1.5">
                          <p className="text-[10px] text-slate-500">Release Pressure</p>
                          <p className="text-sm font-bold text-slate-200">{b.pressureReleaseBar} bar</p>
                        </div>
                      )}
                      {b.saturationEfficiencyPct && (
                        <div className="bg-slate-800/40 rounded px-2 py-1.5">
                          <p className="text-[10px] text-slate-500">Saturation Eff.</p>
                          <p className="text-sm font-bold text-emerald-400">{b.saturationEfficiencyPct}</p>
                        </div>
                      )}
                      {b.airFlowRateNlMin !== null && (
                        <div className="bg-slate-800/40 rounded px-2 py-1.5">
                          <p className="text-[10px] text-slate-500">Air Flow</p>
                          <p className="text-sm font-bold text-slate-200">{b.airFlowRateNlMin} NL/min</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Operating Parameters */}
          {operatingParams && (
            <Card className="bg-rb-surface border-slate-800/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-bold text-white flex items-center gap-2">
                  <Beaker className="w-4 h-4 text-emerald-400" /> Operating Parameters
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {[
                  { label: 'Rated Flow', value: operatingParams.ratedFlowM3h, unit: 'm³/h', alt: operatingParams.ratedFlowGpm, altUnit: 'GPM' },
                  { label: 'Rated Pressure', value: operatingParams.ratedPressureBar, unit: 'bar', alt: operatingParams.ratedPressurePsi, altUnit: 'psi' },
                  { label: 'Recycle Ratio', value: operatingParams.recycleRatioPct, unit: '', string: true },
                  { label: 'A/S Ratio', value: operatingParams.airToSolidsRatio, unit: 'g/kg', string: true },
                  { label: 'Hydraulic Loading', value: operatingParams.hydraulicLoading, unit: 'GPM/ft²', string: true },
                  { label: 'Surface Loading', value: operatingParams.surfaceLoading, unit: 'm³/m²·h', string: true },
                  { label: 'Float Dry Matter', value: operatingParams.floatDryMatterPct, unit: '%', string: true },
                ].map((item, i) => {
                  if (!item.value && !item.string) return null;
                  if (item.string && !item.value) return null;
                  return (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-800/50 last:border-0">
                      <span className="text-xs text-slate-400">{item.label}</span>
                      <span className="text-xs text-slate-200 font-medium">
                        {item.string ? item.value : `${item.value} ${item.unit}`}
                        {!item.string && item.alt && <span className="text-slate-500 ml-1">({item.alt} {item.altUnit})</span>}
                      </span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Dimensions */}
          {dimensions && (
            <Card className="bg-rb-surface border-slate-800/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-bold text-white flex items-center gap-2">
                  <Ruler className="w-4 h-4 text-purple-400" /> Dimensions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {dimensions.lengthMm !== null && dimensions.widthMm !== null && dimensions.heightMm !== null && (
                  <div className="bg-slate-800/40 rounded p-2">
                    <p className="text-[10px] text-slate-500 mb-1">L × W × H</p>
                    <p className="text-sm font-bold text-white">
                      {dimensions.lengthMm} × {dimensions.widthMm} × {dimensions.heightMm} mm
                    </p>
                    <p className="text-[10px] text-slate-500">
                      ({dimensions.lengthIn}" × {dimensions.widthIn}" × {dimensions.heightIn}")
                    </p>
                  </div>
                )}
                {dimensions.weightLbs !== null && (
                  <div className="flex items-center justify-between py-1.5 border-b border-slate-800/50">
                    <span className="text-xs text-slate-400">Weight</span>
                    <span className="text-xs text-slate-200 font-medium">{dimensions.weightLbs} lbs</span>
                  </div>
                )}
                {dimensions.shippingWeightLbs !== null && (
                  <div className="flex items-center justify-between py-1.5 border-b border-slate-800/50">
                    <span className="text-xs text-slate-400">Shipping Weight</span>
                    <span className="text-xs text-slate-200 font-medium">{dimensions.shippingWeightLbs} lbs</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sibling Models */}
        {siblingModels.length > 0 && (
          <Card className="bg-rb-surface border-slate-800/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold text-white flex items-center gap-2">
                <Cog className="w-4 h-4 text-slate-400" /> Other Models in {model.seriesName}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {siblingModels.map(sib => (
                  <button
                    key={sib.id}
                    onClick={() => navigate(`/pumps/${sib.id}`)}
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-800/40 hover:bg-slate-700/50 transition-colors text-left"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-200">{sib.code}</p>
                      <p className="text-[10px] text-slate-500">
                        {sib.motorKw !== null ? `${sib.motorKw} kW` : ''}
                        {sib.motorKw !== null && sib.maxFlowM3h !== null ? ' · ' : ''}
                        {sib.maxFlowM3h !== null ? `${sib.maxFlowM3h} m³/h` : ''}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-600" />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Curve Metadata */}
        {curveMeta && (
          <div className="text-[10px] text-slate-600 flex flex-wrap gap-3">
            <span>Test fluid: {curveMeta.testFluid}</span>
            <span>Temp: {curveMeta.testTempC}°C</span>
            <span>Density: {curveMeta.testDensity} kg/m³</span>
            <span>Viscosity: {curveMeta.testViscosity} mm²/s</span>
            <span>Curve types: {curveMeta.curveTypesAvailable}</span>
          </div>
        )}
      </motion.div>
    </div>
  );
}


